/**
 * StatCard — KPI-ийн ЦОРЫН ГАНЦ хэлбэр.
 *
 * Өмнө нь `ui.tsx`-д нэг, `dashboard.tsx`-д өөр `Kpi` байсныг нэгтгэв.
 * Шинэ KPI хэрэгтэй бол ЭНЭ компонентыг дуудна, хуудсандаа дахин
 * зохиохгүй.
 *
 * ⚠️ Өсөлт/бууралтын ӨНГӨ нь чиглэлээс биш, `good` талбараас гарна.
 *    Рублийн ханш ӨСӨХ нь импортлогчид МУУ мэдээ — сум нь дээшээ,
 *    өнгө нь улаан ягаан байх ёстой. Чиглэлээр өнгө сонгодог болгвол
 *    яг эсрэгээр нь ойлгуулна.
 *
 * Босоо тэнцвэр: карт `h-full` + доод хэсэг `mt-auto` тул grid дотор
 * янз бүрийн урттай кирилл шошготой картууд ёроолоороо шулуун зэрэгцэнэ.
 *
 * ⚠️ ӨНГӨ нь ТӨЛӨВ заана, ангилал БИШ. `accent` = хэвийн, `alert` =
 *    анхаарал шаардсан, `mute` = утга бэлэн биш. Тиймээс хуудсууд
 *    өнгө дамжуулах шаардлагагүй — `pending` / `delta.good` /
 *    `meterTone`-оос өөрөө гарна. Карт бүрийг өөр өнгөөр будвал өнгө
 *    утгаа алдаж, зүгээр л чимэглэл болно.
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Meter, type MeterTone } from './meter';
import { Sparkline } from './sparkline';
import { Ring } from './ring';
import { CountUp } from './count-up';
import { Icon, type IconName } from './icons';
import { InfoTip } from './info-tip';

export interface StatDelta {
  /** Аль хэдийн форматлагдсан текст — `@boots/domain`-аас */
  text: string;
  direction?: 'up' | 'down' | 'flat';
  /**
   * true → эерэг (оюу), false → анхаарал (ягаан),
   * undefined → тайван тайлбар (саарал)
   */
  good?: boolean;
}

const ARROW = { up: '▲', down: '▼', flat: '·' } as const;

function deltaClass(d: StatDelta): string {
  if (d.good === undefined) return 'text-ink-2';
  return d.good ? 'text-accent-700' : 'text-alert-ink';
}

export type StatTone = 'accent' | 'alert' | 'mute';

const CHIP: Record<StatTone, string> = {
  accent: 'bg-accent-100 text-accent-700',
  alert: 'bg-alert-bg text-alert-ink',
  mute: 'bg-well text-ink-3',
};

const BAR: Record<StatTone, string> = {
  accent: 'bg-accent',
  alert: 'bg-alert',
  mute: 'bg-track',
};

/**
 * Тоныг картын өөрийн төлөвөөс гаргана — 20 дуудлагын аль нь ч өнгө
 * дамжуулах шаардлагагүй. Одоогийн өгөгдлөөр ягаан болох боломжтой
 * нь ГУРАВ: «Нийлүүлэгчийн зээл» (өр байвал), «Sync хийгээгүй» (>0),
 * «Агуулахад» (идэвхгүй ажилтан байвал). Эхний хоёр нь дашбоард дээр
 * ээлжлэн гардаг тул нэг дэлгэцэнд хамгийн ихдээ НЭГ ягаан карт —
 * анхаарал сарниагүй хэвээр.
 */
function resolveTone(pending: boolean, delta?: StatDelta, meterTone?: MeterTone): StatTone {
  if (pending) return 'mute';
  if (delta?.good === false || meterTone === 'alert') return 'alert';
  return 'accent';
}

export function StatCard({
  label,
  value,
  delta,
  meter,
  meterTone = 'accent',
  spark,
  to,
  pending = false,
  footer,
  icon,
  tone,
  info,
  feature = false,
}: {
  label: string;
  /** Форматлагдсан утга. `formatQty` / `formatMnt` / `formatMntShort`. */
  value: string;
  delta?: StatDelta;
  /** 0–100. Өгвөл доод талд тууз гарна. */
  meter?: number;
  meterTone?: MeterTone;
  /** Өгвөл тоон утгын ард жижиг график */
  spark?: number[];
  /** Өгвөл карт бүхэлдээ холбоос болно */
  to?: string;
  /**
   * Утга бүрэн биш — эрхээр нуугдсан (`•••`) эсвэл хараахан
   * тооцоологдоогүй. Картыг бүдгэрүүлж «энэ тоо бэлэн биш» гэдгийг
   * хэлнэ, гэхдээ байрлалыг нь хадгална.
   */
  pending?: boolean;
  footer?: ReactNode;
  /**
   * Өгвөл шошгоны хажууд иконы чип, зүүн зах дээр өнгөт тууз гарна.
   * Өгөөгүй бол карт яг хуучнаараа — 20 дуудлага алхам алхмаар
   * шилжиж чадна.
   */
  icon?: IconName;
  /** Ховор тохиолдолд тоныг гараар. Ердийн үед `resolveTone` шийднэ. */
  tone?: StatTone;
  /**
   * Шошгоны хажуугийн «i» товчинд гарах тайлбар. Тоо нь ил, учир
   * шалтгаан нь энд — карт уртсахгүй.
   *
   * ⚠️ Энд Л байгаа тоо бичихгүй: хулганагүй хүн олохгүй.
   */
  info?: ReactNode;
  /**
   * Дашбоардын том хэлбэр (dark glass): Unbounded тоо 0-ээс гүйнэ,
   * нэгж («сая₮») тусдаа, баруун доор цагираг (`meter`) эсвэл
   * sparkline (`spark`). Бусад хуудас хуучин авсаархан хэлбэрээрээ.
   */
  feature?: boolean;
}) {
  const interactive = to
    ? 'hover:border-accent-300 hover:shadow-panel focus-visible:border-accent-300'
    : '';

  const t = tone ?? resolveTone(pending, delta, meterTone);

  const body = feature ? (
    <FeatureBody
      label={label} value={value} delta={delta} meter={meter} meterTone={meterTone}
      spark={spark} pending={pending} icon={icon} tone={t} info={info}
    />
  ) : (
    <>
      {/*
        Зүүн туузыг `absolute` -аар зурна, `border-l` -ээр БИШ: хүрээ
        зузаатгавал картын дотоод өргөн 104 → 102px болж, «1.2 тэрбум₮»
        гэх урт утгын тасрах эрсдэл нэмэгдэнэ. Тууз нь `px-4` дотор
        давхарладаг тул дотоод өргөн хөндөгдөхгүй.

        Абсолют байрлалтай элемент flex item БИШ тул `gap-1.75`-д
        нөлөөлөхгүй. `hover:border-accent-300` -той зөрчилдөхгүй —
        хүрээ туузны ЗҮҮН талд 1px-ээр үлдэж, тууз түүн дээр суухгүй.
      */}
      {icon ? <span aria-hidden className={`absolute inset-y-0 left-0 w-[3px] ${BAR[t]}`} /> : null}

      {/*
        Икон + шошго нэг мөрөнд. `flex-wrap` + шошгоны `basis-20`
        (80px) нь breakpoint-гүй хамгаалалт: 320px дээр картын дотоод
        өргөн 104px, чип 28 + зай 8 = 36 → шошгонд 68px < 80px үлдэх
        тул шошго ӨӨРӨӨ дараагийн мөрөнд бүтэн өргөнөөр бууна.
        375px дээр 99px үлдэх тул нэг мөрөнд хэвээр.
      */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {icon ? (
          <span
            aria-hidden
            className={`grid size-7 shrink-0 place-items-center rounded-well ${CHIP[t]}`}
          >
            <Icon name={icon} />
          </span>
        ) : null}
        {/* Шошго — кирилл урт байж болно, тайрахгүй боон өгнө */}
        <span className="min-w-0 grow basis-20 text-meta text-ink-2 text-pretty">{label}</span>
        {info ? <InfoTip label={`${label} — дэлгэрэнгүй`}>{info}</InfoTip> : null}
      </div>

      <div className="flex items-end justify-between gap-3">
        {/*
          ⚠️ `whitespace-nowrap` БИШ. `formatMntShort` нь «1.2 тэрбум₮»
          гэх зайтай утга гаргадаг бөгөөд 375px дэлгэц дээр KPI карт
          ~133px дотоод өргөнтэй байдаг тул тасрахгүй бол хуудас
          хэвтээ халина. `break-words` нь өөр гарц байхгүй үед л үг
          дотор тасалдаг — «450,000₮» гэх зайгүй тоо бүтэн үлдэнэ.
        */}
        <span className="num min-w-0 text-metric-sm break-words sm:text-metric">{value}</span>
        {spark && spark.length > 1 ? (
          <span className="h-7 w-16 shrink-0 sm:w-20">
            <Sparkline values={spark} label={`${label} — хандлага`} dot={false} />
          </span>
        ) : null}
      </div>

      {delta ? (
        <span className={`text-meta text-pretty ${pending ? 'text-ink-3' : deltaClass(delta)}`}>
          {delta.direction ? (
            <span aria-hidden className="mr-1 text-micro">
              {ARROW[delta.direction]}
            </span>
          ) : null}
          {delta.text}
        </span>
      ) : null}

      {/* mt-auto — картууд ёроолоороо зэрэгцэнэ */}
      {meter === undefined ? null : (
        <Meter value={meter} tone={meterTone} size="sm" className="mt-auto" />
      )}
      {footer}
    </>
  );

  const className =
    `surface relative flex h-full flex-col overflow-hidden rounded-card transition ` +
    `${feature ? 'gap-3 p-4 sm:min-h-40 sm:px-6 sm:py-5.5' : 'gap-1.75 px-4 py-3.5'} ` +
    `${pending ? 'opacity-70' : ''} ${interactive}`;

  if (to) {
    return (
      <Link to={to} className={`${className} text-left`}>
        {body}
      </Link>
    );
  }

  return <div className={className}>{body}</div>;
}

/**
 * Том KPI-ийн бие — `feature` үед.
 *
 * ⚠️ Тоо нь Unbounded (`font-display`) — тэр фонтод Ө/Ү байхгүй ч энд
 *    зөвхөн тоо, нэгж нь Onest-оор тусдаа тул аюулгүй.
 */
function FeatureBody({
  label, value, delta, meter, meterTone, spark, pending, icon, tone, info,
}: {
  label: string;
  value: string;
  delta?: StatDelta;
  meter?: number;
  meterTone: MeterTone;
  spark?: number[];
  pending: boolean;
  icon?: IconName;
  tone: StatTone;
  info?: ReactNode;
}) {
  return (
    <>
      <div className="flex items-center gap-3">
        {icon ? (
          <span
            aria-hidden
            className={`grid size-8 shrink-0 place-items-center rounded-[10px] border border-current/25 ${CHIP[tone]}`}
          >
            <Icon name={icon} />
          </span>
        ) : null}
        <span className="min-w-0 grow text-body font-medium text-ink-2 text-pretty">{label}</span>
        {info ? <InfoTip label={`${label} — дэлгэрэнгүй`}>{info}</InfoTip> : null}
      </div>

      <div className="mt-auto flex items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-2">
          <span className="flex flex-wrap items-baseline gap-x-2 font-display text-num-md font-semibold sm:text-num-lg">
            <CountUp text={value} unitClassName="font-sans text-h2 font-semibold tracking-normal text-ink-3" />
          </span>
          {delta ? (
            <span className={`text-body font-medium text-pretty ${pending ? 'text-ink-3' : deltaClass(delta)}`}>
              {delta.direction ? (
                <span aria-hidden className="mr-1 text-micro">{ARROW[delta.direction]}</span>
              ) : null}
              {delta.text}
            </span>
          ) : null}
        </div>

        {meter !== undefined ? (
          <Ring value={meter} tone={meterTone === 'alert' ? 'alert' : 'accent'} label={label} />
        ) : spark && spark.length > 1 ? (
          <span className="hidden h-11 w-[120px] shrink-0 sm:block">
            <Sparkline values={spark} label={`${label} — хандлага`} dot={false} smooth animate />
          </span>
        ) : null}
      </div>
    </>
  );
}

/** KPI-уудын мөр — 375px дээр 2 багана, өргөн дэлгэцэд 4. */
export function StatGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 items-stretch gap-3 xl:grid-cols-4 xl:gap-3.5">{children}</div>;
}

/**
 * ⛔ ТҮР ХААГДСАН — юу ч харуулахгүй.
 *
 * Өмнө нь хуудас бүрийн дээд талд «Статистик ▼» товч байж, дарахад
 * KPI карт нээгддэг байв. Захиалагчийн шийдвэр: тоонууд нэг ХУУДАСТАА
 * төвлөрнө (`/stats` — «Статистик» цэс), ажлын хуудас нь зөвхөн ажлаа
 * харуулна.
 *
 * ЯАГААД КОМПОНЕНТ ХЭВЭЭР ҮЛДЭВ
 *  «Түр нуу» гэсэн даалгавар. Хуудаснаас (`items`, `orders`,
 *  `purchasing`, `staff`, `warehouse`, `credit`, `installments`) KPI-г
 *  ТАСДАЖ хаявал буцаахад тэр бүгдийг дахин бичих болно. Энд `null`
 *  буцаах нь нэг мөрөөр эргэж буцах зам.
 *
 *  ⚠️ `finance` ЭНД БАЙХГҮЙ БОЛСОН (2026-09-29): түүний нуусан 4 карт нь
 *     Тайлан, Статистикийн тоог давтдаг байсан тул бүр мөсөн хасав —
 *     сэргээх зүйл үлдээгүй.
 *
 * ⚠️ `children` рендер хийгдэхгүй ч ЭЦЭГ хуудсын `useQuery`-үүд
 *    ажилласан хэвээр (жишээ нь `items.tsx`-ийн `/reports/stock`).
 *    Бүрмөсөн хасах шийдвэр гармагц тэр хүсэлтүүдийг ч цэвэрлэнэ.
 *
 * Сэргээх бол: доорх мөрийг устгаад доод талын хуучин биеийг эргүүлнэ
 * (git түүхэнд бүтнээрээ байгаа).
 */
export function StatPanel(_props: { label?: string; children: ReactNode }) {
  return null;
}
